import { Col, Form, FormInstance, Input, Row } from "antd";
import { useFetchData } from "../hooks/useFetchData";
import TextArea from "antd/es/input/TextArea";
import { Attachments } from "./Attachments";
import { Loader } from "../../../pages/Account/components/View/Admin/HeaderActions/components/EmailsModal/components/Loader";
import { SendToFormItem } from "../../../pages/Account/components/View/Admin/HeaderActions/components/EmailsModal/components/SendToFormItem";
import { useMemo } from "react";
import { usePermissions } from "../../../lib/hooks/usePermissions";
import { useSetMissingFileError } from "../../../lib/hooks/useSetMissingFileError";
import { CancelPanel } from "./CancelPanel";
import { useDateLabelByStatus } from "../hooks/useDateLabel";

type FormItemsProps = {
  id: string;
  mailingType: 1 | 2 | 3;
  form: FormInstance;
  data: any;
  canceled: boolean;
  isSaving: boolean;
  sendToData: any;
  setSendToData: React.Dispatch<React.SetStateAction<any>>;
  setInitialFiles: React.Dispatch<React.SetStateAction<string[]>>;
  setIsSaving: React.Dispatch<React.SetStateAction<boolean>>;
  setData: React.Dispatch<React.SetStateAction<any>>;
  onCanceled: () => void;
};

export const FormItems = ({
  id,
  mailingType,
  form,
  data,
  canceled,
  isSaving,
  sendToData,
  setSendToData,
  setInitialFiles,
  onCanceled,
  setIsSaving,
  setData
}: FormItemsProps) => {
  const { isLoading, sendTo } = useFetchData(id, mailingType, form, setInitialFiles, setData);

  const { hasAccess } = usePermissions();

  useSetMissingFileError(form, "fileList");

  const isDisabled: boolean = useMemo(() => {
    if (!data) {
      return true;
    }

    if (canceled) {
      return false || isSaving;
    }
    if (mailingType === 1) {
      if ([2, 3, 10].includes(data.status)) {
        return true;
      }

      return !hasAccess("MailingEmails", "SaveAndSend") || isSaving;
    } else {
      return true;
    }
  }, [mailingType, data, hasAccess, isSaving, canceled]);

  const showCancelPanel: boolean = useMemo(
    () => mailingType === 3 && !canceled && hasAccess("MailingScheduled", "Cancel"),
    [canceled, hasAccess, mailingType]
  );

  const { dateLabel } = useDateLabelByStatus(data?.status);

  const isScheduled: boolean = useMemo(() => data && data.status === 5, [data]);

  const sendToContainerProps = useMemo(
    () => ({
      span: isScheduled ? 24 : 19,
      className: isScheduled ? "" : "pr-2"
    }),
    [isScheduled]
  );

  return (
    <Row>
      {isLoading ? (
        <Loader />
      ) : (
        <Form
          autoComplete="off"
          layout="vertical"
          requiredMark={false}
          className="w-100 view-email-modal"
          form={form}
          disabled={isDisabled}
        >
          {showCancelPanel && <CancelPanel id={id} onCanceled={onCanceled} data={data} />}

          <Row className="w-100">
            <Col {...sendToContainerProps}>
              <SendToFormItem
                form={form}
                initalData={sendTo}
                optionsData={sendToData}
                setOptionsData={setSendToData}
                clearError={() => {}}
                isEmailView
                status={data.status}
              />
            </Col>
            {!isScheduled && (
              <Col span={5} className="pl-2">
                <Form.Item label={dateLabel} name="date">
                  <Input disabled />
                </Form.Item>
              </Col>
            )}
          </Row>

          <Form.Item label="Email Subject" name="subject" className="w-100">
            <Input disabled />
          </Form.Item>

          <Form.Item label="Message" name="body" className="mb-4">
            <TextArea rows={7} />
          </Form.Item>

          <Attachments mailingType={mailingType} status={data.status} canceled={canceled} setIsSaving={setIsSaving} />
        </Form>
      )}
    </Row>
  );
};
